import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Standard button dimensions and styles to maintain consistency
const BUTTON_STYLES = {
  sm: {
    padding: 'px-4 py-1.5',
    fontSize: 'text-sm',
  },
  md: {
    padding: 'px-6 py-2',
    fontSize: 'text-base',
  },
  lg: {
    padding: 'px-8 py-2.5',
    fontSize: 'text-lg',
  },
  hero: {
    padding: 'px-10 py-4',
    fontSize: 'text-base',
  }
};

// Variant classes with better visibility and alignment with the new gray color scheme
const variantClasses = {
  primary: 'bg-green-600 hover:bg-green-700 text-white border border-white/10 shadow-sm',
  secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-800 border border-gray-300/50 shadow-sm',
  hero: 'bg-green-600/80 backdrop-blur-sm text-white border border-white/50 shadow-md',
  ghost: 'bg-transparent hover:bg-white/20 text-white border border-white/30'
};

/**
 * Reusable Button component with animation effects
 * 
 * @param {Object} props - Component props
 * @param {string} props.text - Button text
 * @param {string} props.to - Link destination (if button is a link)
 * @param {Function} props.onClick - Click handler (if button is not a link)
 * @param {string} props.variant - Button variant ('primary', 'secondary', 'hero', 'ghost')
 * @param {string} props.size - Button size ('sm', 'md', 'lg', 'hero')
 * @param {boolean} props.animate - Whether to add animation effects
 * @param {boolean} props.fullWidth - Whether the button should take full width
 * @param {Object} props.customStyles - Additional custom styles to apply
 * @param {Object} props.state - State to pass to Link component
 */
export const Button = ({ 
  text, 
  to, 
  onClick, 
  variant = 'primary', 
  size = 'md', 
  animate = true,
  fullWidth = false,
  className = '',
  customStyles = {},
  children,
  state = null,
  ...props 
}) => {
  // Size classes
  const sizeClasses = {
    sm: BUTTON_STYLES.sm.padding + ' ' + BUTTON_STYLES.sm.fontSize,
    md: BUTTON_STYLES.md.padding + ' ' + BUTTON_STYLES.md.fontSize,
    lg: BUTTON_STYLES.lg.padding + ' ' + BUTTON_STYLES.lg.fontSize,
    hero: BUTTON_STYLES.hero.padding + ' ' + BUTTON_STYLES.hero.fontSize
  };
  
  // Animation variants
  const buttonVariants = {
    initial: { scale: 0.98, opacity: 0 },
    animate: { 
      scale: 1, 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    hover: { 
      scale: 1.05,
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      y: -2,
      transition: { duration: 0.2 }
    },
    tap: { scale: 0.98 }
  };
  
  // Float animation (used in hero button)
  const floatAnimation = {
    y: [0, -3, 0],
    transition: { 
      y: { repeat: Infinity, duration: 2, ease: "easeInOut" }
    }
  };
  
  // Combine all classes
  const buttonClass = `
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${fullWidth ? 'w-full' : 'w-auto'}
    rounded-full 
    font-['Patrick_Hand']
    tracking-wider
    hover:shadow-lg 
    transition-all 
    duration-300 
    flex 
    items-center 
    justify-center
    ${className}
  `;
  
  // Button content
  const buttonContent = (
    <>
      {children || text}
    </>
  );
  
  // Choose between motion button and regular button
  const MotionButton = ({ children }) => (
    <motion.div
      className={buttonClass}
      initial={animate ? "initial" : undefined}
      animate={animate ? "animate" : undefined}
      whileHover={animate ? "hover" : undefined}
      whileTap={animate ? "tap" : undefined}
      variants={buttonVariants}
      {...(variant === 'hero' && animate ? { animate: floatAnimation } : {})}
      style={customStyles}
      onClick={onClick}
      {...props}
    >
      {children}
    </motion.div>
  );

  // Render as link or button
  if (to) {
    return (
      <Link to={to} className="inline-block" state={state}>
        <MotionButton>
          {buttonContent}
        </MotionButton>
      </Link>
    );
  }
  
  return (
    <MotionButton>
      {buttonContent}
    </MotionButton>
  );
};

/**
 * Hero section action button - TYLKO DO PRZEWIJANIA W DÓŁ
 * Enhanced with modern UX/UI effects
 */
export const HeroActionButton = ({ 
  text = 'ODKRYJ PRZEPISY', 
  size = 'hero',
  className = ''
}) => {
  // Use the standardized button dimensions
  const { padding, fontSize } = BUTTON_STYLES[size];

  // Prosta funkcja przewijania w dół do sekcji kategorii
  const scrollDown = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    
    // Znajdź element po ID
    const categorySection = document.getElementById('categories');
    
    if (categorySection) {
      // Jeśli znaleziono element, scrolluj do niego
      categorySection.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    } else {
      // Jeśli nie znaleziono elementu, użyj window.location.hash jako fallback
      window.location.hash = 'categories';
    }
  };

  return (
    <div className={`inline-block ${className}`}>
      <motion.button
        className={`${padding} bg-gradient-to-r from-green-600 via-green-600 to-emerald-600 hover:from-green-700 hover:via-green-700 hover:to-emerald-700 rounded-full border-2 border-white/30 cursor-pointer
                shadow-2xl hover:shadow-green-500/50 transition-all duration-300 inline-flex items-center justify-center relative overflow-hidden group
                backdrop-blur-sm`}
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ 
          scale: 1,
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.5,
            ease: "easeOut"
          }
        }}
        whileHover={{ 
          scale: 1.1,
          y: -4,
          boxShadow: "0 20px 40px rgba(34, 197, 94, 0.4), 0 0 20px rgba(34, 197, 94, 0.3)",
          transition: {
            duration: 0.3,
            ease: "easeOut"
          }
        }}
        whileTap={{ 
          scale: 0.95,
          y: 0,
          transition: {
            duration: 0.1
          }
        }}
        onClick={scrollDown}
      >
        {/* Animated gradient background */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-green-400/20 via-emerald-400/20 to-green-400/20"
          animate={{
            x: ['-100%', '200%'],
          }}
          transition={{
            x: {
              repeat: Infinity,
              duration: 3,
              ease: "linear"
            }
          }}
        />

        {/* Shimmer effect on hover */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          initial={{ x: '-100%' }}
          whileHover={{ x: '200%' }}
          transition={{
            duration: 0.6,
            ease: "easeInOut"
          }}
        />

        {/* Glow effect */}
        <motion.div
          className="absolute inset-0 rounded-full bg-green-400/20 blur-xl"
          animate={{
            opacity: [0.5, 0.8, 0.5],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Multiple pulse rings */}
        <motion.span 
          className="absolute inset-0 rounded-full border-2 border-green-300/50"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ 
            opacity: [0.6, 0, 0.6], 
            scale: [0.9, 1.3, 0.9],
            transition: { 
              repeat: Infinity, 
              duration: 2,
              ease: "easeInOut"
            }
          }}
        />
        
        <motion.span 
          className="absolute inset-0 rounded-full border-2 border-green-200/40"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ 
            opacity: [0.4, 0, 0.4], 
            scale: [0.95, 1.4, 0.95],
            transition: { 
              repeat: Infinity, 
              duration: 2.5,
              ease: "easeInOut",
              delay: 0.3
            }
          }}
        />

        {/* Button content with icon */}
        <motion.span 
          className={`relative z-10 font-['Patrick_Hand'] ${fontSize} text-white tracking-wider drop-shadow-lg uppercase px-4 sm:px-6 whitespace-nowrap font-bold flex items-center gap-2`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          {text}
          <motion.svg
            className="w-4 h-4 sm:w-5 sm:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            initial={{ x: 0 }}
            whileHover={{ x: 4 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </motion.svg>
        </motion.span>

        {/* Ripple effect on click */}
        <motion.div
          className="absolute inset-0 rounded-full bg-white/30"
          initial={{ scale: 0, opacity: 0 }}
          whileTap={{
            scale: 2,
            opacity: [0.5, 0],
            transition: {
              duration: 0.6
            }
          }}
        />
      </motion.button>
    </div>
  );
};

/**
 * Info section action button - TYLKO DO NAWIGACJI
 */
export const InfoActionButton = ({ 
  text = 'ODKRYJ PRZEPISY', 
  to = '/kuchnia',
  size = 'lg' 
}) => {
  // Use the standardized button dimensions
  const { padding, fontSize } = BUTTON_STYLES[size];

  // Bezpośrednia nawigacja z przewinięciem na górę
  const navigate = () => {
    // Przekierowanie do innej strony za pomocą window.location
    window.location.href = to;
    // Przewinięcie na górę strony
    window.scrollTo(0, 0);
  };

  return (
    <div className="inline-block">
      <motion.div
        className={`${padding} bg-green-600/80 backdrop-blur-sm rounded-full border border-white/50 cursor-pointer
                shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center justify-center relative`}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ 
          scale: [1, 1.05, 1],
          opacity: 1,
          transition: {
            scale: { 
              repeat: Infinity, 
              duration: 1.5, 
              ease: "easeInOut",
              repeatType: "reverse" 
            },
            opacity: { duration: 0.3 }
          }
        }}
        whileHover={{ 
          backgroundColor: "rgba(22, 163, 74, 0.9)",
          scale: 1.08,
          transition: {
            duration: 0.2
          }
        }}
        whileTap={{ scale: 0.97 }}
        onClick={navigate}
      >
        {/* Pulse ring effect */}
        <motion.span 
          className="absolute inset-0 rounded-full border-2 border-green-400/40"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ 
            opacity: [0.7, 0, 0.7], 
            scale: [0.85, 1.2, 0.85],
            transition: { 
              repeat: Infinity, 
              duration: 2,
              ease: "easeInOut"
            }
          }}
        />
        
        <motion.span 
          className={`font-['Patrick_Hand'] ${fontSize} text-white tracking-wider drop-shadow-md uppercase px-2 whitespace-nowrap`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.2 }}
        >
          {text}
        </motion.span>
      </motion.div>
    </div>
  );
};

export default Button; 